"use client"

import type React from "react"

import { observer } from "mobx-react-lite"
import { useAppContext } from "../../../shared/functions/Context"
import { useState, useEffect, type FormEvent } from "react"
import MODAL_NAMES from "../ModalName"
import { hideModalFromId } from "../../../shared/functions/ModalShow"
import { defaultQualification, type IQualification } from "../../../shared/models/Qualifications"

import { X, FileText, Trash2, Eye } from "lucide-react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../dashboard/company-dashboard/ui/card"
import { Button } from "../../dashboard/company-dashboard/ui/button"
import { Label } from "../../dashboard/company-dashboard/ui/label"
import { Input } from "../../dashboard/company-dashboard/ui/input"
import { Textarea } from "../../dashboard/company-dashboard/ui/textarea"

const AddPersonalQualification = observer(() => {
  const { store, api } = useAppContext()
  const me = store.auth.me?.asJson

  const [qualification, setQualification] = useState<IQualification>({
    ...defaultQualification,
    attachment: [],
  })
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (store.qualification.selected) {
      setQualification(store.qualification.selected)
    }
  }, [store.qualification.selected])

  const handleQualificationChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setQualification({ ...qualification, [e.target.name]: e.target.value })
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      const uploadedFileLinks = await Promise.all(
        Array.from(files).map(async (file) => {
          const uploadedFileLink = await api.uploadManager.uploadQualificationToStorage(
            file,
            `trainingQualification/${me?.uid}`,
          )
          return uploadedFileLink
        }),
      )

      setQualification({
        ...qualification,
        attachment: [...qualification.attachment, ...uploadedFileLinks],
      })
    }
  }

  const handleRemoveAttachment = (index: number) => {
    const updatedAttachments = [...qualification.attachment]
    updatedAttachments.splice(index, 1)
    setQualification({
      ...qualification,
      attachment: updatedAttachments,
    })
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (me?.uid) {
      const newQualification: IQualification = {
        ...qualification,
        uid: me.uid,
      }

      setLoading(true)
      try {
        if (store.qualification.selected) {
          await api.qualifications.update(newQualification)
        } else {
          await api.qualifications.create(newQualification)
        }
      } catch (error) {
        console.error("Error updating personal qualification:", error)
      } finally {
        setLoading(false)
        onCancel()
      }
    }
  }

  const onCancel = () => {
    store.personalDevelopment.clearSelected()
    hideModalFromId(MODAL_NAMES.EXECUTION.PERSONAL_DEVELOPMENT_QUALIFICATIONS_MODAL)
  }

  return (
    <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full mx-auto p-8">
      <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="relative">
          <Button variant="ghost" size="icon" className="absolute right-4 top-4" onClick={onCancel}>
            <X className="h-4 w-4" />
          </Button>
          <CardTitle>Add Personal Qualifications</CardTitle>
          <CardDescription>Add your educational qualifications and certifications</CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="courseName">Course Name</Label>
                <Input
                  id="courseName"
                  name="courseName"
                  placeholder="Enter course name"
                  value={qualification.courseName}
                  onChange={handleQualificationChange}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="institution">Institution</Label>
                <Input
                  id="institution"
                  name="institution"
                  placeholder="Enter institution name"
                  value={qualification.institution}
                  onChange={handleQualificationChange}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="startDate">Start Date</Label>
                <Input
                  id="startDate"
                  name="startDate"
                  type="date"
                  value={qualification.startDate}
                  onChange={handleQualificationChange}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="endDate">End Date</Label>
                <Input
                  id="endDate"
                  name="endDate"
                  type="date"
                  value={qualification.endDate}
                  onChange={handleQualificationChange}
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="certificateObtained">Certificate Obtained</Label>
                <Input
                  id="certificateObtained"
                  name="certificateObtained"
                  placeholder="Enter certificate obtained"
                  value={qualification.certificateObtained}
                  onChange={handleQualificationChange}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="comments">Comments</Label>
              <Textarea
                id="comments"
                name="comments"
                rows={3}
                placeholder="Add any comments"
                value={qualification.comments}
                onChange={handleQualificationChange}
              />
            </div>

            <div className="space-y-4">
              <Label htmlFor="documents">Upload Document(s)</Label>
              <Input id="documents" type="file" multiple onChange={handleFileChange} className="cursor-pointer" />

              {qualification.attachment.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Uploaded Attachments</h4>
                  <div className="space-y-2">
                    {qualification.attachment.map((url, index) => (
                      <div key={index} className="flex items-center justify-between rounded-lg border p-3">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">Attachment {index + 1}</span>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => handleRemoveAttachment(index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? "Saving..." : "Save"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
})

export default AddPersonalQualification
